import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { plugin as VueTippy } from 'vue-tippy';
import { clickOutside } from './directives/click-outside';
import i18n from './i18n';
import { initSentry } from './plugins/sentry';
import { initClarity } from './plugins/clarity';
import { initChatwoot } from './plugins/chatwoot';
import { initAppVersionCheck } from './utils/appVersion';

import 'tippy.js/dist/tippy.css';

import './config/axiosConfig';
import './assets/scss/main.scss';

// Create the app instance
const app = createApp(App);

// Initialize Sentry for error tracking (BetterStack)
initSentry(app, router);

// Microsoft Clarity (dashboard only): set VITE_CLARITY_PROJECT_ID in env, or VITE_CLARITY_ENABLED=true to test in dev
initClarity();

// Chatwoot chat (bottom-right widget): prod-only by default, set VITE_CHATWOOT_ENABLED=true to test in dev
initChatwoot();

// Detect nieuwe deploys en herlaad gecachte (bookmark/PWA) sessies automatisch.
// NB: niet op import.meta.env.PROD gaten — de .env's zetten NODE_ENV=development
// waardoor PROD ook in productiebuilds false is. import.meta.hot bestaat alleen
// in de dev-server, dus dit draait in élke build maar nooit tijdens `vite dev`.
if (!import.meta.hot) {
  initAppVersionCheck(router);
}

// Add global error handler (Sentry will also capture these)
app.config.errorHandler = (err, instance, info) => {
  console.error('Global error:', err);
  console.error('Error info:', info);
};

window.onerror = function (message, source, lineno, colno, error) {
  console.error('Window error:', { message, source, lineno, colno, error });
};

// Create a fresh Pinia instance
const pinia = createPinia();

// Install plugins
app.use(pinia);
app.use(router);
app.use(i18n);

// Make $t globally available in all components
app.config.globalProperties.$t = i18n.global.t;
app.config.devtools = import.meta.env.VITE_NODE_ENV === 'development';
app.directive('click-outside', clickOutside);
app.use(
  VueTippy,
  // optional
  {
    directive: 'tippy', // => v-tippy
    component: 'tippy', // => <tippy/>
    componentSingleton: 'tippy-singleton', // => <tippy-singleton/>,
    defaultProps: {
      placement: 'auto-end',
      allowHTML: true,
    },
  }
);

// Initialize the app
const init = async () => {
  try {
    app.mount('#funnelflow');
  } catch (error) {
    const body = document.querySelector('body');
    body.innerHTML = `<h1>Error: ${error.message}</h1>`;
    console.error('Failed to initialize app:', error);
  }
};

init();
